import {
  Page,
  PageBody,
  PageHeader,
  PageHeaderIconTile,
  PageHeaderTabs,
  PageHeaderTitle,
  Stack,
  TabNav,
  TabNavList,
  TabNavTab,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import {
  availableDeliveryChannels,
  DeliveryFilters,
  DeliveryTable,
  ExportDeliveriesButton,
  useDeliveryActions,
} from '@/features/deliveries';
import { useAuth } from '@/services/auth';
import { hasUnlimitedPlanFeatureAccess } from '@/services/auth/plan-features';
import { PlanFeature } from '@/services/auth/types';
import type { Delivery } from '@/services/db/delivery/entity';
import { useDeliveries } from '@/services/db/delivery/queries';
import { buildDeliveriesListParams } from '@/services/db/delivery/utils';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { buildEmptyMessage, hasActiveFilters, toExportInput, toFilterInput, useOutboxFilters } from './hooks';
import type { OutboxTab } from './types';

type OutboxPageProps = {
  /** Which tab is active — selects the drafts filter and the active tab. */
  tab: OutboxTab;
  /** Builds the href for each tab (the route owns the URL segment vocabulary). */
  tabHref: (tab: OutboxTab) => string;
  /** Builds a row's detail href (deliveries → React detail; drafts → Ember draft). */
  getDeliveryHref: (delivery: Delivery) => string;
};

/**
 * The Outbox page — the Deliveries/Sent (FPS-2591) and Drafts (FPS-2594) tabs,
 * both composed from the `features/deliveries` blocks. Ported from the Ember
 * `outbox/list` shell + `<cio-deliveries>`: one shared component parametrized by
 * tab. The route owns the URL segment vocabulary and passes the tab + the href
 * builders in.
 */
export default function OutboxPage({ tab, tabHref, getDeliveryHref }: OutboxPageProps) {
  const workspaceId = requireWorkspaceId();
  const { hasPermission } = usePermissions();
  // Match the Ember gates: export needs `journeys/exports/create`; the bulk send/retry/delete
  // actions need `journeys/messages/send` (its absence is the "read-only" state).
  const canExport = hasPermission('journeys/exports/create') === true;
  const canSendMessages = hasPermission('journeys/messages/send') === true;
  const taxonomy = useProductTaxonomy();
  const { values, onChange } = useOutboxFilters();
  const auth = useAuth();
  const isLiveNotificationsAvailable =
    auth.account != null && hasUnlimitedPlanFeatureAccess(auth.account, PlanFeature.LiveNotifications);
  const channels = availableDeliveryChannels(isLiveNotificationsAvailable);

  const isDrafts = tab === 'drafts';
  const params = buildDeliveriesListParams({ ...toFilterInput(values), drafts: isDrafts ? true : undefined });
  const { deliveries, isLoading, isError, refetch, hasMore, fetchMore, isFetchingMore } = useDeliveries({ params });
  const actions = useDeliveryActions({ deliveries, deliveryStatus: values.deliveryStatus, workspaceId });

  return (
    <Page>
      <PageHeader>
        <PageHeaderIconTile name="delivery-log" />
        <PageHeaderTitle>{taxonomy.t('nav.review.outbox')}</PageHeaderTitle>
        <PageHeaderTabs>
          <TabNav aria-label={taxonomy.t('nav.review.outbox')}>
            <TabNavList>
              <TabNavTab href={tabHref('deliveries')} isActive={tab === 'deliveries'}>
                {taxonomy.t('nav.review.outbox.deliveries')}
              </TabNavTab>
              <TabNavTab href={tabHref('drafts')} isActive={tab === 'drafts'}>
                {taxonomy.t('nav.review.outbox.drafts')}
              </TabNavTab>
            </TabNavList>
          </TabNav>
        </PageHeaderTabs>
      </PageHeader>
      <PageBody>
        <Stack gap="300">
          <DeliveryFilters
            values={values}
            onChange={onChange}
            channels={channels}
            drafted={isDrafts}
            isFilteringByEmailEnabled={!isDrafts}
            fixed={isDrafts ? 'deliveryStatus,userActions' : undefined}
            maxRange={isDrafts ? '30 days' : undefined}
            dateRangeTooltipMessage={isDrafts ? 'Drafts are stored only for 30 days' : undefined}
            actions={
              <ExportDeliveriesButton
                exportInput={{ ...toExportInput(values), drafted: isDrafts ? true : undefined }}
                from={isDrafts ? 'outbox_drafts' : 'outbox_deliveries'}
                workspaceId={workspaceId}
                isDisabled={!canExport}
              />
            }
          />
          <DeliveryTable
            deliveries={deliveries}
            isLoading={isLoading}
            isError={isError}
            onReload={() => void refetch()}
            hasMore={hasMore}
            onLoadMore={() => void fetchMore()}
            isLoadingMore={isFetchingMore}
            getDeliveryHref={getDeliveryHref}
            actionType={values.actionType}
            hasFilters={hasActiveFilters(values)}
            emptyMessage={buildEmptyMessage(
              values,
              isDrafts,
              taxonomy.t('messageActivity.deliveriesLearnMoreNoun').toLowerCase(),
            )}
            isDrafted={isDrafts}
            hasReadOnlyPermission={!canSendMessages}
            onBulkRetry={actions.onBulkRetry}
            onBulkSend={actions.onBulkSend}
            onBulkDelete={actions.onBulkDelete}
            onBulkRedeliver={actions.onBulkRedeliver}
            onRowRetry={actions.onRowRetry}
            onRowDelete={actions.onRowDelete}
          />
        </Stack>
      </PageBody>
    </Page>
  );
}
